import { computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'

export type PublishedLegendProps = ModelProps<PublishedLegend>

/**
 * A Legend that has been published for consumption.
 */
export class PublishedLegend extends Model<PublishedLegend> {
  id = Id.none

  @observable accessor version = -1
  @observable accessor workspaceId = Id.none
  @observable accessor name = 'Legend'
  @observable accessor description = ''
  @observable accessor inactive = false
  @observable accessor serviceType: LegendServiceType = 'Misc'

  /**
   * Legend version ID.
   */
  @computed
  get versionId(): LegendVersionId {
    if (!this.id) {
      return LegendVersionId.none
    }

    return {
      id: Id.assert(this.id),
      version: this.version,
    }
  }
}

/**
 * Legend version ID.
 */
export interface LegendVersionId {
  readonly id: string
  readonly version: number
}

/**
 * Creates a Legend Version ID.
 *
 * @param id
 * @param version
 */
export function LegendVersionId(id: string, version: number): LegendVersionId {
  return {
    id,
    version,
  }
}

/**
 * Whether 2 Legend Version IDs are considered the same.
 */
LegendVersionId.equals = function equals(
  left: LegendVersionId,
  right: LegendVersionId,
) {
  return (
    left === right || (left.id === right.id && left.version === right.version)
  )
}

/**
 * Asserts the Legend Version ID is non-empty.
 */
LegendVersionId.assert = function assert(
  input: LegendVersionId | undefined | null,
): LegendVersionId {
  if (
    LegendVersionId.equals(input ?? LegendVersionId.none, LegendVersionId.none)
  ) {
    throw new TypeError(
      'A non-empty Legend Version ID was expected, but was empty.',
    )
  }
  return input!
}

/**
 * Formats the Legend Version ID as a string.
 *
 * @param legendVersionId
 */
LegendVersionId.asString = function asString(legendVersionId: LegendVersionId) {
  return `${legendVersionId.id}:${legendVersionId.version}`
}

/**
 * No ID.
 */
LegendVersionId.none = {
  id: 'none',
  version: -1,
} as LegendVersionId
